import { supabase } from '../supabaseClient';
import { MAX_REQUIRED_GUARDS, getDefaultTimeZoneForCountry } from '../utils/siteScheduling';
import {
  createPolygonFeature,
  getPolygonCentroid,
  parseGeometryPolygon,
  type GeoJSONPolygon,
  type LatLngTuple,
} from '../utils/geo';

/** Every site read returns the same joined shape, so the projection is shared. */
const SITE_SELECT = `
  *,
  client_contract:client_contracts!sites_client_contract_id_fkey(
    id,
    name,
    status
  ),
  required_licence:licence_types!sites_required_licence_type_id_fkey(
    id,
    name,
    country_code
  )
`;

export type SiteGeofenceMode = 'radius' | 'polygon';
export type SiteWelfareMode = 'automatic' | 'always' | 'off';

export interface SiteRow {
  id: string;
  company_id: string;
  client_contract_id?: string | null;
  client_contract?: {
    id: string;
    name: string;
    status?: string | null;
  } | null;
  name: string;
  address: string | null;
  place_id: string | null;
  country_code?: string | null;
  timezone?: string | null;
  required_coverage_start_minute?: number | null;
  required_coverage_end_minute?: number | null;
  /** Guards the coverage window needs on site at once. */
  required_guard_count?: number | null;
  welfare_check_mode?: SiteWelfareMode | null;
  welfare_night_start_minute?: number | null;
  welfare_night_end_minute?: number | null;
  required_licence_type_id?: string | null;
  required_licence?: {
    id: string;
    name: string;
    country_code: string | null;
  } | null;
  attendance_radius_m?: number | null;
  geofence_type?: SiteGeofenceMode | null;
  geofence_radius_m?: number | null;
  geofence_polygon?: GeoJSONPolygon | null;
  location?: string | null;
  [key: string]: unknown;
}

export interface LicenceTypeRow {
  id: string;
  name: string;
  country_code: string;
  authority: string;
  is_active: boolean;
}

export interface CreateSiteParams {
  clientContractId?: string | null;
  name: string;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string | null;
  countryCode?: string | null;
  timezone?: string | null;
  coverageStartMinute?: number;
  coverageEndMinute?: number;
  requiredGuardCount?: number;
  welfareCheckMode?: SiteWelfareMode;
  welfareNightStartMinute?: number;
  welfareNightEndMinute?: number;
  requiredLicenceTypeId?: string | null;
  geofenceMode?: SiteGeofenceMode;
  geofenceRadiusM?: number | null;
  geofencePolygon?: LatLngTuple[] | null;
}

/** The site builder edits every field in one pass, so the update mirrors create. */
export type UpdateSiteParams = Omit<CreateSiteParams, 'clientContractId'>;

export interface FetchSitesOptions {
  includeArchivedClients?: boolean;
}

function toRequiredLicence(value: unknown): SiteRow['required_licence'] {
  const record = Array.isArray(value) ? value[0] : value;
  if (!record || typeof record !== 'object') return null;

  return {
    id: (record as { id: string }).id,
    name: (record as { name: string }).name,
    country_code: ((record as { country_code?: string | null }).country_code ?? null),
  };
}

function toClientContract(value: unknown): SiteRow['client_contract'] {
  const record = Array.isArray(value) ? value[0] : value;
  if (!record || typeof record !== 'object') return null;

  return {
    id: (record as { id: string }).id,
    name: (record as { name: string }).name,
    status: ((record as { status?: string | null }).status ?? null),
  };
}

function toSiteRow(row: Record<string, unknown>): SiteRow {
  const polygonPositions = parseGeometryPolygon(row.geofence_polygon);

  return {
    ...(row as SiteRow),
    client_contract_id: (row.client_contract_id as string | null) ?? null,
    client_contract: toClientContract(row.client_contract),
    required_licence: toRequiredLicence(row.required_licence),
    geofence_type: row.geofence_type === 'polygon' ? 'polygon' : 'radius',
    geofence_radius_m: (row.geofence_radius_m as number | null) ?? (row.attendance_radius_m as number | null) ?? 300,
    geofence_polygon: polygonPositions ? createPolygonFeature(polygonPositions)?.geometry ?? null : null,
  };
}

/** Maps builder params onto site columns. Create and update write the same set,
 *  so the derivation (polygon centroid fallback, radius clamping) lives in one place. */
function toSiteColumns({
  name,
  address,
  latitude,
  longitude,
  placeId,
  countryCode,
  timezone,
  coverageStartMinute,
  coverageEndMinute,
  requiredGuardCount = 1,
  welfareCheckMode = 'automatic',
  welfareNightStartMinute = 1320,
  welfareNightEndMinute = 480,
  requiredLicenceTypeId,
  geofenceMode = 'radius',
  geofenceRadiusM = 300,
  geofencePolygon,
}: UpdateSiteParams): Record<string, unknown> {
  const polygonFeature = geofencePolygon?.length ? createPolygonFeature(geofencePolygon) : null;
  const derivedCenter = (latitude == null || longitude == null)
    ? getPolygonCentroid(geofencePolygon ?? [])
    : null;
  const resolvedLatitude = latitude ?? derivedCenter?.[0] ?? null;
  const resolvedLongitude = longitude ?? derivedCenter?.[1] ?? null;
  const resolvedGeofenceMode: SiteGeofenceMode = geofenceMode === 'polygon' ? 'polygon' : 'radius';
  const resolvedGeofenceRadius = Math.max(1, Math.round(geofenceRadiusM ?? 300));

  const columns: Record<string, unknown> = {
    name,
    address,
    place_id: placeId ?? null,
    country_code: countryCode ?? '',
    timezone: timezone ?? getDefaultTimeZoneForCountry(countryCode),
    required_coverage_start_minute: coverageStartMinute ?? 0,
    required_coverage_end_minute: coverageEndMinute ?? 1440,
    required_guard_count: Math.min(Math.max(Math.round(requiredGuardCount), 1), MAX_REQUIRED_GUARDS),
    welfare_check_mode: welfareCheckMode,
    welfare_night_start_minute: welfareNightStartMinute,
    welfare_night_end_minute: welfareNightEndMinute,
    required_licence_type_id: requiredLicenceTypeId ?? null,
    attendance_radius_m: resolvedGeofenceRadius,
    geofence_type: resolvedGeofenceMode,
    geofence_radius_m: resolvedGeofenceRadius,
    geofence_polygon: resolvedGeofenceMode === 'polygon' ? polygonFeature?.geometry ?? null : null,
  };

  if (resolvedLatitude != null && resolvedLongitude != null) {
    columns['location'] = `SRID=4326;POINT(${resolvedLongitude} ${resolvedLatitude})`;
  }

  return columns;
}

export const siteService = {
  async getCurrentUserCompanyId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return (data as { company_id: string }).company_id;
  },

  async fetchAllSites(clientContractId?: string | null, options: FetchSitesOptions = {}): Promise<SiteRow[]> {
    const companyId = await this.getCurrentUserCompanyId();
    let query = supabase
      .from('sites')
      .select(SITE_SELECT)
      .eq('company_id', companyId)
      .order('name');

    if (clientContractId) {
      query = query.eq('client_contract_id', clientContractId);
    }

    const { data, error } = await query;

    if (error) throw error;
    const rows = ((data ?? []) as Record<string, unknown>[]).map(toSiteRow);
    return options.includeArchivedClients
      ? rows
      : rows.filter((site) => site.client_contract?.status !== 'archived');
  },

  async createSite({ clientContractId, ...params }: CreateSiteParams): Promise<SiteRow> {
    const companyId = await this.getCurrentUserCompanyId();

    const { data, error } = await supabase
      .from('sites')
      .insert({
        ...toSiteColumns(params),
        company_id: companyId,
        client_contract_id: clientContractId ?? null,
      })
      .select(SITE_SELECT)
      .single();

    if (error) throw error;
    return toSiteRow(data as Record<string, unknown>);
  },

  async fetchLicenceTypes(countryCode: string): Promise<LicenceTypeRow[]> {
    const trimmedCountryCode = countryCode.trim().toUpperCase();
    if (!trimmedCountryCode) return [];

    const { data, error } = await supabase
      .from('licence_types')
      .select('id, name, country_code, authority, is_active')
      .eq('country_code', trimmedCountryCode)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data ?? []) as LicenceTypeRow[];
  },

  async updateSite(siteId: string, params: UpdateSiteParams): Promise<SiteRow> {
    const { data, error } = await supabase
      .from('sites')
      .update(toSiteColumns(params))
      .eq('id', siteId)
      .select(SITE_SELECT)
      .single();

    if (error) throw error;
    return toSiteRow(data as Record<string, unknown>);
  },

  async deleteSite(siteId: string): Promise<void> {
    // 1. Delete patrol-related data for patrols at this site
    const { data: patrols } = await supabase
      .from('patrols')
      .select('id')
      .eq('site_id', siteId);

    const patrolIds = (patrols ?? []).map((p: { id: string }) => p.id);

    if (patrolIds.length > 0) {
      const { data: runs } = await supabase
        .from('patrol_runs')
        .select('id')
        .in('patrol_id', patrolIds);

      const runIds = (runs ?? []).map((r: { id: string }) => r.id);

      if (runIds.length > 0) {
        const { error: eventsErr } = await supabase
          .from('patrol_checkpoint_events')
          .delete()
          .in('patrol_run_id', runIds);
        if (eventsErr) throw eventsErr;

        const { error: runsErr } = await supabase
          .from('patrol_runs')
          .delete()
          .in('patrol_id', patrolIds);
        if (runsErr) throw runsErr;
      }

      const { error: pcErr } = await supabase
        .from('patrol_checkpoints')
        .delete()
        .in('patrol_id', patrolIds);
      if (pcErr) throw pcErr;

      const { error: patrolsErr } = await supabase
        .from('patrols')
        .delete()
        .eq('site_id', siteId);
      if (patrolsErr) throw patrolsErr;
    }

    // 2. Delete checkpoint QR codes, then checkpoints
    const { data: checkpoints } = await supabase
      .from('checkpoints')
      .select('id')
      .eq('site_id', siteId);

    const checkpointIds = (checkpoints ?? []).map((c: { id: string }) => c.id);

    if (checkpointIds.length > 0) {
      const { error: qrErr } = await supabase
        .from('checkpoint_qr_codes')
        .delete()
        .in('checkpoint_id', checkpointIds);
      if (qrErr) throw qrErr;

      const { error: cpErr } = await supabase
        .from('checkpoints')
        .delete()
        .eq('site_id', siteId);
      if (cpErr) throw cpErr;
    }

    // 3. Delete duty slots (no FK to sites but clean up; cascades to assignments)
    const { error: dutyErr } = await supabase
      .from('duty_slots')
      .delete()
      .eq('site_id', siteId);
    if (dutyErr) throw dutyErr;

    // 4. Delete break slots (cascades to break_slot_assignments)
    const { error: breakErr } = await supabase
      .from('break_slots')
      .delete()
      .eq('site_id', siteId);
    if (breakErr) throw breakErr;

    // 5. Delete the site (cascades to posts, shifts, shift_segments)
    const { error } = await supabase
      .from('sites')
      .delete()
      .eq('id', siteId);
    if (error) throw error;
  },
};
